import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Trophy, Sparkles, Crown, Factory, Instagram, Youtube, Music2, Twitter, Upload, CheckCircle2, Loader2, Send, ArrowDown, Scale, Users, Rocket, CalendarClock, PoundSterling, ShieldAlert } from 'lucide-react';
import Seo from '../components/Seo';
import useReveal from '../hooks/useReveal';
import { useLang } from '../context/LanguageContext';
import { PhoneInput, COUNTRIES, validatePhone } from '../components/PhoneInput';
import { getCompetitionCopy, getWizCopy } from '../i18n/competition';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  name: '', email: '', phone: '', country: '',
  instagram: '', instagram_followers: '', tiktok: '', tiktok_followers: '',
  youtube: '', youtube_followers: '', twitter: '', twitter_followers: '',
  total_reach: '', cover_letter: '',
};

const DEADLINE = new Date('2026-10-31T23:59:59Z').getTime();
const calcCountdown = () => {
  const diff = DEADLINE - Date.now();
  if (diff <= 0) return null;
  return { Days: Math.floor(diff / 86400000), Hours: Math.floor((diff / 3600000) % 24), Mins: Math.floor((diff / 60000) % 60), Secs: Math.floor((diff / 1000) % 60) };
};

const field = 'w-full bg-transparent border-b border-espresso/20 py-3 text-espresso placeholder-mocha/50 focus:outline-none focus:border-gold transition-colors';
const PRIZE_ICONS = [Crown, Sparkles, Factory, PoundSterling];
const CHIP_ICONS = [Users, Scale, ShieldAlert];

export default function Competition() {
  const { lang } = useLang();
  const c = getCompetitionCopy(lang);
  const w = getWizCopy(lang);
  useReveal(lang);
  const [form, setForm] = useState(initial);
  const [country, setCountry] = useState('GB');
  const [cvFile, setCvFile] = useState(null);
  const [status, setStatus] = useState('idle');
  const [errMsg, setErrMsg] = useState('');
  const fileRef = useRef(null);
  const [step, setStep] = useState(1);
  const partialId = useRef('');
  const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

  const fullPhone = () => {
    const digits = form.phone.replace(/\D/g, '');
    const dial = (COUNTRIES.find((x) => x.iso === country) || {}).dial || '44';
    return digits ? `+${dial} ${digits}` : '';
  };

  const savePartial = () => {
    axios.post(`${API}/competition/partial`, { ...form, phone: fullPhone(), partial_id: partialId.current || null })
      .then((r) => { if (r.data?.id) partialId.current = r.data.id; })
      .catch(() => {});
  };

  const goToStep = (n) => {
    setErrMsg('');
    setStep(n);
    document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' });
  };

  const next1 = () => {
    if (!form.name.trim() || !EMAIL_RE.test(form.email.trim()) || !form.country) { setErrMsg(w.errBasics); return; }
    if (!validatePhone(form.phone.replace(/\D/g, ''))) { setErrMsg(c.errPhone); return; }
    if (!form.instagram && !form.tiktok && !form.youtube && !form.twitter) { setErrMsg(c.errSocial); return; }
    savePartial();
    goToStep(2);
  };

  const next2 = () => {
    if (!form.cover_letter.trim()) { setErrMsg(w.errCover); return; }
    savePartial();
    goToStep(3);
  };
  const [countdown, setCountdown] = useState(calcCountdown);
  const startTracked = useRef(false);
  const trackStart = () => {
    if (startTracked.current) return;
    startTracked.current = true;
    let vid = 'anon';
    try { vid = localStorage.getItem('arge_vid') || 'anon'; } catch {}
    axios.post(`${API}/track`, { visitor_id: vid, path: '/competition', event: 'competition_start' }).catch(() => {});
  };
  useEffect(() => {
    const id = setInterval(() => setCountdown(calcCountdown()), 1000);
    return () => clearInterval(id);
  }, []);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.split('.').pop().toLowerCase();
    if (!['pdf', 'doc', 'docx'].includes(ext)) { setErrMsg(c.errCvType); setCvFile(null); return; }
    if (f.size > 10 * 1024 * 1024) { setErrMsg(c.errCvSize); setCvFile(null); return; }
    setErrMsg(''); setCvFile(f);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrMsg('');
    if (!cvFile) { setErrMsg(c.errCv); return; }
    const fd = new FormData();
    Object.entries({ ...form, phone: fullPhone(), partial_id: partialId.current }).forEach(([k, v]) => fd.append(k, v));
    fd.append('cv', cvFile);
    setStatus('sending');
    try {
      await axios.post(`${API}/competition/apply`, fd);
      if (window.gtag) window.gtag('event', 'competition_application', {});
      setStatus('success');
      setForm(initial); setCvFile(null); partialId.current = ''; setStep(1);
    } catch (err) {
      setStatus('idle');
      setErrMsg(err?.response?.data?.detail || c.errGeneric);
    }
  };

  const scrollToApply = () => document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' });

  const socials = [
    { icon: Instagram, handle: 'instagram', followers: 'instagram_followers', ph: c.phIg, fph: c.followers },
    { icon: Music2, handle: 'tiktok', followers: 'tiktok_followers', ph: c.phTt, fph: c.followers },
    { icon: Youtube, handle: 'youtube', followers: 'youtube_followers', ph: c.phYt, fph: c.subscribers },
    { icon: Twitter, handle: 'twitter', followers: 'twitter_followers', ph: c.phX, fph: c.followers },
  ];

  return (
    <div>
      <Seo title="Influencer Competition — Launch Your Own Brand For Free" path="/competition"
        description="ARGE Cosmetics is giving one influencer a fully funded brand launch: free branding + up to 4 luxury products of your choice (300–500 units each), produced to your spec and sold through our shop. You take the profit. Apply by 31 October 2026."
        image="/og-competition.jpg" />

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center pt-28 pb-20 bg-espresso overflow-hidden">
        <img src="/images/competition_hero.jpg" alt="ARGE Influencer Competition" className="absolute inset-0 w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-r from-espresso via-espresso/80 to-espresso/30" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold text-espresso text-[0.68rem] tracking-[0.3em] uppercase font-medium mb-6 animate-pulse" data-testid="competition-hero-badge">
              <Trophy className="w-3.5 h-3.5" /> {c.badge}
            </span>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-ivory leading-[1.05]">
              {c.heroT1}<br /><span className="text-gold-light italic">{c.heroT2}</span>
            </h1>
            <p className="mt-6 text-lg text-ivory/85 leading-relaxed max-w-xl">{c.heroP}</p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <button onClick={scrollToApply} data-testid="competition-hero-apply-btn" className="group inline-flex items-center gap-3 px-10 py-4 bg-gold text-espresso text-xs tracking-[0.2em] uppercase font-medium hover:bg-gold-light transition-colors">
                {c.applyNow} <ArrowDown className="w-4 h-4 group-hover:translate-y-0.5 transition-transform" />
              </button>
              <span className="inline-flex items-center gap-2 text-ivory/80 text-sm"><CalendarClock className="w-4 h-4 text-gold-light" /> {c.closes}</span>
            </div>
          </div>
        </div>
      </section>

      {/* PRIZES */}
      <section className="py-20 md:py-28 bg-ivory">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14 reveal">
            <span className="inline-flex items-center gap-3 text-[0.7rem] tracking-[0.35em] uppercase text-gold"><span className="w-8 h-px bg-gold" />{c.prizeK}<span className="w-8 h-px bg-gold" /></span>
            <h2 className="font-serif text-4xl md:text-5xl text-espresso mt-4">{c.prizeT}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {c.prizes.map((p, i) => {
              const Icon = PRIZE_ICONS[i];
              return (
                <div key={p.t} className="reveal bg-cream p-8 border-t-2 border-gold hover:-translate-y-1 transition-transform duration-300" data-testid={`prize-card-${i}`}>
                  <Icon className="w-8 h-8 text-gold mb-5" />
                  <h3 className="font-serif text-2xl text-espresso">{p.t}</h3>
                  <p className="mt-3 text-sm text-mocha leading-relaxed">{p.d}</p>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-12 reveal">
            <button onClick={scrollToApply} className="inline-flex items-center gap-3 px-10 py-4 bg-espresso text-ivory text-xs tracking-[0.2em] uppercase hover:bg-gold transition-colors" data-testid="competition-prizes-apply-btn">
              {c.launchCta} <Rocket className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-28 bg-espresso text-ivory">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14 reveal">
            <span className="inline-flex items-center gap-3 text-[0.7rem] tracking-[0.35em] uppercase text-gold-light"><span className="w-8 h-px bg-gold" />{c.howK}<span className="w-8 h-px bg-gold" /></span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4">{c.howT}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {c.steps.map((s, i) => (
              <div key={i} className="reveal border-l border-gold/40 pl-6">
                <p className="font-serif text-5xl text-gold/50">{String(i + 1).padStart(2, '0')}</p>
                <h3 className="font-serif text-2xl mt-4 text-gold-light">{s.t}</h3>
                <p className="mt-3 text-sm text-ivory/75 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-14 grid sm:grid-cols-3 gap-6 text-center">
            {c.chips.map((chip, i) => {
              const Icon = CHIP_ICONS[i];
              return (
                <div key={i} className="bg-ivory/5 border border-gold/25 p-6"><Icon className="w-6 h-6 text-gold-light mx-auto mb-3" /><p className="text-sm text-ivory/85">{chip}</p></div>
              );
            })}
          </div>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section id="apply" className="py-20 md:py-28 bg-gradient-to-b from-cream to-ivory">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12 reveal">
            <span className="inline-flex items-center gap-3 text-[0.7rem] tracking-[0.35em] uppercase text-gold"><span className="w-8 h-px bg-gold" />{c.applyK}<span className="w-8 h-px bg-gold" /></span>
            <h2 className="font-serif text-4xl md:text-5xl text-espresso mt-4">{c.applyT}</h2>
            <p className="mt-4 text-mocha">{c.applySub}</p>
            {countdown && (
              <div className="mt-8 flex justify-center gap-2 sm:gap-4" data-testid="competition-page-countdown">
                {Object.entries(countdown).map(([label, v]) => (
                  <div key={label} className="w-[72px] sm:w-24 bg-espresso py-3 sm:py-4">
                    <p className="font-serif text-3xl sm:text-5xl text-gold-light tabular-nums">{String(v).padStart(2, '0')}</p>
                    <p className="text-[0.58rem] sm:text-[0.65rem] tracking-[0.25em] uppercase text-ivory/70 mt-1">{c.cd[label]}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {status === 'success' ? (
            <div className="text-center bg-ivory border-t-2 border-gold p-14" data-testid="competition-success">
              <CheckCircle2 className="w-14 h-14 text-gold mx-auto mb-5" />
              <h3 className="font-serif text-3xl text-espresso">{c.okT}</h3>
              <p className="mt-3 text-mocha max-w-md mx-auto">{c.okP}</p>
              <Link to="/shop" className="inline-block mt-7 px-8 py-3 border border-espresso/30 text-espresso text-xs tracking-[0.18em] uppercase hover:border-gold hover:text-gold transition-colors">{c.explore}</Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} onFocusCapture={trackStart} className="bg-ivory p-8 md:p-12 border-t-2 border-gold shadow-sm" data-testid="competition-form">
              {/* PROGRESS */}
              <div className="mb-10" data-testid="wizard-progress">
                <div className="flex justify-between items-end mb-3">
                  {w.steps.map((label, i) => (
                    <div key={i} className={`flex items-center gap-2 ${i + 1 <= step ? 'text-espresso' : 'text-mocha/50'}`}>
                      <span className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-semibold ${i + 1 < step ? 'bg-gold text-espresso' : i + 1 === step ? 'bg-espresso text-ivory' : 'bg-espresso/10 text-mocha'}`}>
                        {i + 1 < step ? '✓' : i + 1}
                      </span>
                      <span className="hidden sm:inline text-[0.65rem] tracking-[0.15em] uppercase">{label}</span>
                    </div>
                  ))}
                </div>
                <div className="h-1 bg-espresso/10 overflow-hidden">
                  <div className="h-full bg-gold transition-all duration-500" style={{ width: `${(step / 3) * 100}%` }} />
                </div>
                <p className="mt-2 text-[0.65rem] tracking-[0.2em] uppercase text-mocha">{w.stepOf.replace('{n}', step)}</p>
              </div>

              {step === 1 && (
                <div className="animate-fade-up" data-testid="wizard-step-1">
                  <p className="text-sm text-gold mb-6 flex items-center gap-2"><Sparkles className="w-4 h-4" /> {w.note1}</p>
                  <h3 className="text-xs tracking-[0.25em] uppercase text-gold mb-6">{c.s1}</h3>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <input name="name" value={form.name} onChange={onChange} placeholder={c.phName} className={field} data-testid="comp-name" />
                    <input type="email" name="email" value={form.email} onChange={onChange} placeholder={c.phEmail} className={field} data-testid="comp-email" />
                    <PhoneInput country={country} onCountry={setCountry} value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} placeholder={c.phPhone} />
                    <select name="country" value={form.country} onChange={onChange} className={`${field} cursor-pointer`} data-testid="comp-country">
                      <option value="">{c.phCountry}</option>
                      {COUNTRIES.map((x) => <option key={x.iso} value={x.name}>{x.name}</option>)}
                    </select>
                  </div>

                  <h3 className="text-xs tracking-[0.25em] uppercase text-gold mt-12 mb-2">{c.s2}</h3>
                  <p className="text-xs text-mocha mb-6">{c.s2n}</p>
                  <div className="space-y-5">
                    {socials.map((s) => (
                      <div key={s.handle} className="flex flex-wrap items-end gap-4">
                        <s.icon className="w-5 h-5 text-gold mb-3 shrink-0" />
                        <input name={s.handle} value={form[s.handle]} onChange={onChange} placeholder={s.ph} className={`${field} flex-1 min-w-[200px]`} data-testid={`comp-${s.handle}`} />
                        <input name={s.followers} value={form[s.followers]} onChange={onChange} placeholder={s.fph} className={`${field} w-full sm:w-40`} data-testid={`comp-${s.followers}`} />
                      </div>
                    ))}
                    <input name="total_reach" value={form.total_reach} onChange={onChange} placeholder={c.phReach} className={field} data-testid="comp-total-reach" />
                  </div>

                  {errMsg && <p className="mt-5 text-sm text-destructive" data-testid="comp-error">{errMsg}</p>}
                  <button type="button" onClick={next1} data-testid="wizard-next-1"
                    className="group mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 px-12 py-4 bg-espresso text-ivory text-xs tracking-[0.2em] uppercase hover:bg-gold transition-colors">
                    {w.cont} <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform rtl:rotate-180" />
                  </button>
                </div>
              )}

              {step === 2 && (
                <div className="animate-fade-up" data-testid="wizard-step-2">
                  <p className="text-sm text-gold mb-6 flex items-center gap-2"><Sparkles className="w-4 h-4" /> {w.note2.replace('{name}', form.name.trim().split(' ')[0] || '')}</p>
                  <h3 className="text-xs tracking-[0.25em] uppercase text-gold mb-2">{c.s3}</h3>
                  <p className="text-sm text-mocha mb-4">{w.coverShort}</p>
                  <textarea name="cover_letter" value={form.cover_letter} onChange={onChange} rows={4} placeholder={w.coverPh} className={`${field} resize-none`} data-testid="comp-cover-letter" />
                  {errMsg && <p className="mt-5 text-sm text-destructive" data-testid="comp-error">{errMsg}</p>}
                  <div className="mt-8 flex flex-wrap gap-4">
                    <button type="button" onClick={() => goToStep(1)} data-testid="wizard-back-2"
                      className="inline-flex items-center gap-2 px-8 py-4 border border-espresso/25 text-espresso text-xs tracking-[0.2em] uppercase hover:border-gold hover:text-gold transition-colors">{w.back}</button>
                    <button type="button" onClick={next2} data-testid="wizard-next-2"
                      className="group flex-1 sm:flex-none inline-flex items-center justify-center gap-3 px-12 py-4 bg-espresso text-ivory text-xs tracking-[0.2em] uppercase hover:bg-gold transition-colors">
                      {w.cont} <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform rtl:rotate-180" />
                    </button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="animate-fade-up" data-testid="wizard-step-3">
                  <p className="text-sm text-gold mb-6 flex items-center gap-2"><Trophy className="w-4 h-4" /> {w.note3}</p>
                  <h3 className="text-xs tracking-[0.25em] uppercase text-gold mb-4">{c.s4}</h3>
                  <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" onChange={pickFile} className="hidden" data-testid="comp-cv-input" />
                  <button type="button" onClick={() => fileRef.current?.click()} data-testid="comp-cv-button"
                    className={`w-full flex items-center justify-center gap-3 border border-dashed px-6 py-7 text-sm transition-colors ${cvFile ? 'border-gold bg-cream text-espresso' : 'border-espresso/30 text-mocha hover:border-gold hover:text-gold'}`}>
                    {cvFile ? <><CheckCircle2 className="w-5 h-5 text-gold" /> {cvFile.name}</> : <><Upload className="w-5 h-5" /> {c.cvBtn}</>}
                  </button>
                  {errMsg && <p className="mt-5 text-sm text-destructive" data-testid="comp-error">{errMsg}</p>}
                  <p className="mt-6 text-[0.7rem] text-mocha/80 leading-relaxed">{c.consent}</p>
                  <div className="mt-7 flex flex-wrap gap-4">
                    <button type="button" onClick={() => goToStep(2)} data-testid="wizard-back-3"
                      className="inline-flex items-center gap-2 px-8 py-4 border border-espresso/25 text-espresso text-xs tracking-[0.2em] uppercase hover:border-gold hover:text-gold transition-colors">{w.back}</button>
                    <button type="submit" disabled={status === 'sending'} data-testid="comp-submit"
                      className="group flex-1 sm:flex-none inline-flex items-center justify-center gap-3 px-12 py-4 bg-gold text-espresso font-semibold text-xs tracking-[0.2em] uppercase hover:bg-gold-light transition-colors disabled:opacity-70">
                      {status === 'sending' ? <><Loader2 className="w-4 h-4 animate-spin" /> {c.submitting}</> : <>{c.submit} <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform rtl:rotate-180" /></>}
                    </button>
                  </div>
                </div>
              )}
            </form>
          )}
        </div>
      </section>

      {/* TERMS */}
      <section className="py-16 md:py-20 bg-cream">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 reveal">
          <h2 className="font-serif text-3xl text-espresso mb-8">{c.termsT}</h2>
          <ol className="space-y-4" data-testid="competition-terms">
            {c.terms.map((t, i) => (
              <li key={i} className="flex gap-4 text-sm text-mocha leading-relaxed">
                <span className="font-serif text-gold text-lg leading-none mt-0.5">{String(i + 1).padStart(2, '0')}</span>{t}
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-espresso text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-serif text-4xl md:text-5xl text-ivory">{c.finalPre} <span className="text-gold-light italic">{c.finalAcc}</span> {c.finalPost}</h2>
          <p className="mt-4 text-ivory/75">{c.finalP}</p>
          <button onClick={scrollToApply} data-testid="competition-final-apply-btn" className="mt-8 inline-flex items-center gap-3 px-12 py-4 bg-gold text-espresso text-xs tracking-[0.2em] uppercase font-medium hover:bg-gold-light transition-colors">
            {c.launchCta} <Trophy className="w-4 h-4" />
          </button>
        </div>
      </section>
    </div>
  );
}
